/**
 * Compliance Data Service
 * 
 * Service for loading compliance rule descriptions and documentation.
 * Currently loads from static JSON files, designed for future database migration.
 * 
 * TODO: Database Migration
 * - Replace JSON file loading with database API calls
 * - Add caching mechanism for better performance
 * - Add real-time updates from database
 */

export interface RuleDescription {
  code: string;
  title: string;
  description: string;
  regulationType: string;
  category: string;
}

export interface ComplianceData {
  rules: Record<string, RuleDescription>;
  _metadata: {
    version: string;
    lastUpdated: string;
    note: string;
  };
}

class ComplianceDataService {
  private static instance: ComplianceDataService;
  private cachedData: ComplianceData | null = null;

  private constructor() {}

  public static getInstance(): ComplianceDataService {
    if (!ComplianceDataService.instance) {
      ComplianceDataService.instance = new ComplianceDataService();
    }
    return ComplianceDataService.instance;
  }

  /**
   * Load rule descriptions from JSON file
   * TODO: Database Migration - Replace with API call to fetch from database
   */
  public async loadRuleDescriptions(): Promise<ComplianceData> {
    if (this.cachedData) {
      return this.cachedData;
    }

    try {
      // TODO: Database Migration - Replace with database API call
      // Example: const response = await fetch('/api/compliance/rule-descriptions');
      const response = await fetch('/compliance-data/rule-descriptions.json');
      
      if (!response.ok) {
        throw new Error(`Failed to load rule descriptions: ${response.statusText}`);
      }

      const data: ComplianceData = await response.json();
      this.cachedData = data;
      return data;
    } catch (error) {
      console.error('Error loading rule descriptions:', error);
      
      // Fallback to default descriptions if JSON file fails to load
      return this.getDefaultRuleDescriptions();
    }
  }

  /**
   * Get rule description by rule ID
   * TODO: Database Migration - Add direct database query for single rule
   */
  public async getRuleDescription(ruleId: string): Promise<RuleDescription | null> {
    const data = await this.loadRuleDescriptions();
    return data.rules[ruleId] || null;
  }

  /**
   * Clear cache to force reload
   * TODO: Database Migration - Add cache invalidation for database updates
   */
  public clearCache(): void {
    this.cachedData = null;
  }

  /**
   * Fallback default descriptions if JSON loading fails
   */
  private getDefaultRuleDescriptions(): ComplianceData {
    return {
      rules: {
        "ADA_604.2": {
          code: "IBC 604.2",
          title: "ADA Toilet Side Wall Clearance (IBC 604.2)",
          description: "Validates that ADA toilet centerline is 16\" minimum to 18\" maximum from the side wall per IBC 604.2 requirements.",
          regulationType: "ADA",
          category: "Restrooms"
        }
      },
      _metadata: {
        version: "1.0",
        lastUpdated: "2025-08-30",
        note: "Default fallback descriptions"
      }
    };
  }
}

export const complianceDataService = ComplianceDataService.getInstance();